/**
 * Plan Upgrade Page
 * 
 * Shows subscription plan options with features and pricing
 * Fetches real data from the API
 */

import React, { useState, useEffect } from 'react'
import { Check, Zap, Users, MessageSquare, HardDrive, Bot, Crown, Sparkles, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { getUserPlans, upgradePlan, type UserPlansResponse } from '../../services/plansApi'


export const PlanUpgrade: React.FC = () => {
  const [plansData, setPlansData] = useState<UserPlansResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  // Fetch plans data on mount
  useEffect(() => {
    fetchPlansData()
  }, [])

  const fetchPlansData = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await getUserPlans()
      
      // Debug: Log the API response to see what we're getting
      console.log('=== USER PLANS API RESPONSE ===')
      console.log('Full response:', JSON.stringify(data, null, 2))
      console.log('Current subscription:', data.current_subscription)
      console.log('Plan display name:', data.current_subscription?.plan_display_name)
      console.log('Plan status:', data.current_subscription?.status)
      console.log('================================')
      
      setPlansData(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription plans')
      console.error('Error fetching plans:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatLimit = (limit: number) => {
    if (limit === -1) return 'Unlimited'
    if (limit >= 1000) return `${limit/1000}k`
    return limit.toString()
  }

  const formatStorageLimit = (limitMB: number) => {
    if (limitMB === -1) return 'Unlimited'
    if (limitMB >= 1024) return `${limitMB/1024}GB`
    return `${limitMB}MB`
  }

  const formatPrice = (priceInCents: number) => (priceInCents / 100).toFixed(0)

  const displayPrice = (planMonthlyCents: number) => {
    if (billingCycle === 'monthly') return `$${formatPrice(planMonthlyCents)}`
    // Yearly with 20% off
    const yearly = Math.round(planMonthlyCents * 12 * 0.8)
    return `$${formatPrice(yearly)} /yr`
  }

  const handleUpgrade = async (planId: string, planName: string) => {
    setIsUpgrading(true)
    
    try {
      const result = await upgradePlan(planId)
      
      // Debug: Log the API response
      console.log('Plan upgrade API response:', result)
      
      // If we have Paddle checkout data, open Paddle checkout
      if (result.paddle_price_id && result.paddle_customer_id) {
        // Load Paddle.js if not already loaded
        if (!window.Paddle) {
          const script = document.createElement('script')
          script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
          script.async = true
          document.head.appendChild(script)
          
          await new Promise((resolve) => {
            script.onload = resolve
          })
        }
        
        // Initialize Paddle with the newer API (like in the example)
        console.log('Setting Paddle environment to sandbox')
        window.Paddle.Environment.set("sandbox")
        
        console.log('Initializing Paddle with token:', result.paddle_client_token)
        window.Paddle.Initialize({
          token: result.paddle_client_token,
          eventCallback: function(data: any) {
            console.log('Paddle event:', data)
            if (data.name === 'checkout.completed') {
              // Show success message
              setSuccessMessage('🎉 Payment successful! Your subscription is now active.')
              setError(null)
              
              // Refresh plans data to show updated current plan
              setTimeout(() => {
                fetchPlansData()
                setSuccessMessage(null) // Clear success message after refresh
              }, 2000) // Wait 2 seconds for webhook processing
              
            } else if (data.name === 'checkout.closed') {
              console.log('Checkout closed by user')
            } else if (data.name === 'checkout.payment.failed') {
              setError('❌ Payment failed. Please try again or use a different payment method.')
              setSuccessMessage(null)
            }
          }
        })
        
        // Open Paddle checkout
        console.log('Opening Paddle checkout with:', {
          priceId: result.paddle_price_id,
          customerId: result.paddle_customer_id
        })
        // Use Paddle.js checkout directly (like in the working example)
        window.Paddle.Checkout.open({
          items: [{
            priceId: result.paddle_price_id,
            quantity: 1
          }],
          customer: {
            id: result.paddle_customer_id
          }
        })
      } else {
        // Fallback for non-checkout upgrades
        alert(result.message)
        await fetchPlansData()
      }
    } catch (err: any) {
      alert(`Failed to upgrade plan: ${err.message}`)
      console.error('Error upgrading plan:', err)
    } finally {
      setIsUpgrading(false)
    }
  }

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'free': return <Sparkles className="h-6 w-6 text-muted-foreground" />
      case 'starter': return <Zap className="h-6 w-6 text-blue-500" />
      case 'professional': return <Crown className="h-6 w-6 text-purple-500" />
      case 'business': return <Users className="h-6 w-6 text-green-500" />
      case 'enterprise': return <Bot className="h-6 w-6 text-orange-500" />
      default: return <Sparkles className="h-6 w-6 text-muted-foreground" />
    }
  }

  const getPlanBadge = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'professional':
        return { text: 'Most Popular', color: 'bg-blue-100 text-blue-800' }
      case 'business':
        return { text: 'Best Value', color: 'bg-green-100 text-green-800' }
      case 'enterprise':
        return { text: 'Custom', color: 'bg-purple-100 text-purple-800' }
      default:
        return null
    }
  }

  const getFeatures = (planName: string) => {
    // Define features based on plan type
    const baseFeatures = [
      { name: 'AI-powered responses', included: true },
      { name: 'Basic analytics', included: true },
      { name: 'Email support', included: true }
    ]

    switch (planName.toLowerCase()) {
      case 'free':
        return [
          ...baseFeatures,
          { name: 'Custom branding', included: false },
          { name: 'Advanced analytics', included: false },
          { name: 'Priority support', included: false }
        ]
      case 'starter':
        return [
          ...baseFeatures,
          { name: 'Advanced analytics', included: true },
          { name: 'Custom branding', included: true },
          { name: 'API access', included: false },
          { name: 'Priority support', included: false }
        ]
      case 'professional':
        return [
          { name: 'Everything in Starter', included: true },
          { name: 'Multiple chatbots', included: true },
          { name: 'Team collaboration', included: true },
          { name: 'API access', included: true },
          { name: 'Priority support', included: true }
        ]
      case 'business':
        return [
          { name: 'Everything in Professional', included: true },
          { name: 'Advanced integrations', included: true },
          { name: 'Custom workflows', included: true },
          { name: 'Dedicated support', included: true },
          { name: 'SLA guarantee', included: true }
        ]
      case 'enterprise':
        return [
          { name: 'Everything in Business', included: true },
          { name: 'Unlimited everything', included: true },
          { name: 'On-premise deployment', included: true },
          { name: 'Custom integrations', included: true },
          { name: 'Dedicated support team', included: true }
        ]
      default:
        return baseFeatures
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Failed to Load Plans</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchPlansData} variant="outline">
            Try Again
          </Button>
        </Card>
      </div>
    )
  }

  // No data state
  if (!plansData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-muted-foreground">No subscription plans available</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-muted/50 to-muted border border-border p-8 text-center space-y-5">
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground">Choose Your Plan</h1>
        <p className="text-base text-muted-foreground max-w-3xl mx-auto">
          Upgrade your AI assistant with more usage, team members, and advanced features.
          Scale your customer support to the next level.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex items-center justify-center gap-2 bg-card border border-border rounded-full p-1 shadow-sm">
          <button
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${
              billingCycle === 'monthly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 ${
              billingCycle === 'yearly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly <span className="text-emerald-600 font-semibold">–20%</span>
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-emerald-500/5 border border-emerald-500/30 text-emerald-600 px-4 py-3 rounded relative">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Current Plan Badge */}
      <div className="text-center space-y-2">
        <Badge 
          variant="outline" 
          className="px-6 py-3 text-lg font-semibold border-2 border-green-500 bg-emerald-500/10 text-green-800"
        >
          <Crown className="h-5 w-5 mr-2" />
          Currently on {plansData.current_subscription.plan_display_name || 'Free Plan'}
        </Badge>
        
        {plansData.current_subscription.current_period_end && (
          <p className="text-sm text-muted-foreground">
            Active until {new Date(plansData.current_subscription.current_period_end).toLocaleDateString()}
          </p>
        )}
        
        {plansData.current_subscription.status === 'active' && (
          <div className="flex items-center justify-center text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm font-medium">Subscription Active</span>
          </div>
        )}
      </div>

      {/* Plans Grid */}
      {(() => {
        // Reorder plans so the current plan appears in the middle on larger screens
        const plans = [...plansData.available_plans]
        const currentIdx = plans.findIndex(p => p.id === plansData.current_subscription.plan_id)
        const byNameIdx = currentIdx === -1 
          ? plans.findIndex(p => (p.name || '').toLowerCase() === (plansData.current_subscription.plan_name || '').toLowerCase())
          : currentIdx
        if (byNameIdx > -1) {
          const [curr] = plans.splice(byNameIdx, 1)
          // Middle index for typical 5-column layout is 2; for other counts, use floor(len/2)
          const middle = Math.floor(plans.length / 2)
          plans.splice(middle, 0, curr)
        }
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {plans.map((plan) => {
              const rawBadge = getPlanBadge(plan.name)
              const features = getFeatures(plan.name)
              const isCurrentPlan = plansData.current_subscription.plan_id === plan.id
              // Determine upgrade vs downgrade by comparing monthly price
              const currentPlan = plansData.available_plans.find(p => p.id === plansData.current_subscription.plan_id)
                || plansData.available_plans.find(p => (p.name || '').toLowerCase() === (plansData.current_subscription.plan_name || '').toLowerCase())
              const isDowngrade = !!currentPlan && plan.price_monthly < (currentPlan?.price_monthly || 0)
              
              return (
            <Card 
              key={plan.id}
              className={`relative transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
                isCurrentPlan ? 'ring-2 ring-emerald-500 shadow-emerald-100' : 'ring-1 ring-border'
              } bg-card/90 backdrop-blur-sm`}
            >
              {/* Hide marketing badge when this is the current plan */}
              {rawBadge && !isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className={rawBadge.color}>
                    {rawBadge.text}
                  </Badge>
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                  <Badge className="whitespace-nowrap inline-flex items-center gap-1 text-sm font-medium bg-emerald-100 text-emerald-800 px-4 py-1 rounded-full shadow-sm border border-emerald-200">
                    Current Plan
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-5">
                  {getPlanIcon(plan.name)}
                </div>
                
                <CardTitle className="text-xl font-bold text-foreground">
                  {plan.display_name}
                </CardTitle>
                
                <div className="space-y-1">
                  <div className="text-3xl font-extrabold tracking-tight text-foreground">
                    {displayPrice(plan.price_monthly)}
                  </div>
                  {billingCycle === 'yearly' && plan.price_monthly > 0 && (
                    <p className="text-xs text-emerald-600">Save 20% with annual billing</p>
                  )}
                  {plan.price_monthly === 0 && <p className="text-sm text-muted-foreground">No credit card required</p>}
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Key Metrics */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-muted-foreground">
                      <Zap className="h-4 w-4 mr-2" />
                      AI Credits
                    </span>
                    <span className="font-medium">
                      {(plan as any).ai_budget_usd > 0
                        ? `$${(plan as any).ai_budget_usd}`
                        : formatLimit(plan.messages_limit)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-muted-foreground">
                      <Users className="h-4 w-4 mr-2" />
                      Team Members
                    </span>
                    <span className="font-medium">{formatLimit(plan.team_members_limit)}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-muted-foreground">
                      <Bot className="h-4 w-4 mr-2" />
                      Agents
                    </span>
                    <span className="font-medium">{formatLimit(plan.chatbots_limit)}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground">Features:</h4>
                  <ul className="space-y-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm">
                        <Check 
                          className={`h-4 w-4 mr-2 ${
                            feature.included ? 'text-green-500' : 'text-muted-foreground/50'
                          }`} 
                        />
                        <span className={feature.included ? 'text-foreground' : 'text-muted-foreground'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleUpgrade(plan.id, plan.name)}
                  disabled={isCurrentPlan || isUpgrading}
                  className={`w-full ${
                    isCurrentPlan
                      ? 'bg-green-600 hover:bg-green-700'
                      : isDowngrade
                      ? 'bg-gray-700 hover:bg-gray-800'
                      : rawBadge
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-800 hover:bg-gray-900'
                  }`}
                  variant={isCurrentPlan ? 'outline' : 'default'}
                >
                  {isUpgrading
                    ? 'Processing...'
                    : isCurrentPlan
                    ? 'Current Plan'
                    : isDowngrade
                    ? `Downgrade to ${plan.name.toLowerCase()}`
                    : `Upgrade to ${plan.name.toLowerCase()}`}
                </Button>

                {plan.name.toLowerCase() === 'enterprise' && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Contact sales for custom pricing
                  </p>
                )}
              </CardContent>
            </Card>
            )})}
          </div>
        )
      })()}

      {/* FAQ Section */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-center text-foreground mb-8">
          Frequently Asked Questions
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div>
            <h3 className="font-semibold text-foreground mb-2">Can I change plans anytime?</h3>
            <p className="text-muted-foreground text-sm">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-foreground mb-2">What happens to my data?</h3>
            <p className="text-muted-foreground text-sm">
              All your chatbots, conversations, and settings are preserved when you change plans.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-foreground mb-2">Do you offer refunds?</h3>
            <p className="text-muted-foreground text-sm">
              Yes, we offer a 30-day money-back guarantee for all paid plans.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-foreground mb-2">Need a custom solution?</h3>
            <p className="text-muted-foreground text-sm">
              Contact our sales team for enterprise features, custom integrations, and volume discounts.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
